import { useCallback, useMemo } from 'react';
import { Calendar, dayjsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './styles.scss';
import { useTranslation } from 'react-i18next';
import { RptAgendaCommercialeDto } from '@api/client';
import { ESITO_COLOR } from '../../_utils/esitoLabel';

const DnDCalendar = withDragAndDrop(Calendar);

dayjs.locale('it');
const localizer = dayjsLocalizer(dayjs);

interface Props {
  data: RptAgendaCommercialeDto[];
  date: Date;
  onNavigate: (date: Date) => void;
  onAddAction: (date: Date) => void;
  onSelectEvent: (item: RptAgendaCommercialeDto) => void;
  onDropEvent: (item: RptAgendaCommercialeDto, newDate: Date) => void;
}

interface AgendaEvent {
  title: string;
  start: Date;
  end: Date;
  resource: RptAgendaCommercialeDto;
}

const PENDING_BG = '#BFBFBF';

const colorForEvent = (esito?: string) => esito ? (ESITO_COLOR[esito] ?? PENDING_BG) : PENDING_BG;

interface ToolbarProps {
  date: Date;
  view: string;
  onNavigate: (action: string) => void;
  onView: (view: string) => void;
}

const CustomToolbar: React.FC<ToolbarProps> = ({ date, onNavigate }) => {
  const label = dayjs(date).format('MMMM YYYY');
  const capitalized = label.charAt(0).toUpperCase() + label.slice(1);

  return (
    <div className="rbc-toolbar">
      <div className="rbc-btn-group">
        <button type="button" onClick={() => onNavigate('PREV')} aria-label="Mese precedente">
          <i className="fas fa-chevron-left" style={{ fontSize: '0.8rem' }} />
        </button>
        <button type="button" onClick={() => onNavigate('TODAY')}>Oggi</button>
        <button type="button" onClick={() => onNavigate('NEXT')} aria-label="Mese successivo">
          <i className="fas fa-chevron-right" style={{ fontSize: '0.8rem' }} />
        </button>
      </div>
      <span className="rbc-toolbar-label agenda-toolbar-title">{capitalized}</span>
    </div>
  );
};

const messages = {
  allDay: 'Tutto il giorno',
  date: 'Data',
  time: 'Ora',
  event: 'Evento',
  noEventsInRange: 'Nessuna attività in questo periodo',
  showMore: (total: number) => `+ altri ${total}`,
};

const EventCard: React.FC<{ event: AgendaEvent }> = ({ event }) => {
  const dto = event.resource;
  const hasEsito = !!dto.esito;

  return (
    <div className="agenda-event-card" style={{ color: '#fff' }}>
      <span className="agenda-event-client">
        {dto.nomeAccount || dto.clienteId || '—'}
      </span>
      <span className="agenda-event-status">
        {hasEsito
          ? <i className="fas fa-check-circle" title={dto.esito ?? ''} />
          : <i className="fas fa-clock agenda-event-status--pending" />
        }
      </span>
    </div>
  );
};

const AgendaActionsCalendar: React.FC<Props> = ({ data, date, onNavigate, onAddAction, onSelectEvent, onDropEvent }) => {
  const { t } = useTranslation();

  const events: AgendaEvent[] = useMemo(() => {
    return data
      .filter((item) => item.actionDate)
      .map((item) => {
        const base = dayjs(item.actionDate!);
        return {
          title: item.nomeAccount || item.clienteId || t('Attività'),
          start: base.hour(9).minute(0).second(0).toDate(),
          end: base.hour(18).minute(0).second(0).toDate(),
          resource: item,
        };
      });
  }, [data]);

  const eventStyleGetter = (event: AgendaEvent) => {
    return {
      style: {
        backgroundColor: colorForEvent(event.resource.esito),
        borderRadius: '6px',
        border: 'none',
        padding: '0',
      },
    };
  };

  const DateHeader = useCallback(
    ({ date, label }: { date: Date; label: string }) => (
      <div className="rbc-date-header-custom">
        <span>{label}</span>
        <button
          className="rbc-add-btn"
          title={t('Nuova attività')}
          onClick={(e) => {
            e.stopPropagation();
            onAddAction(date);
          }}
        >
          +
        </button>
      </div>
    ),
    [onAddAction]
  );

  const dayPropGetter = useCallback((date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today ? { className: 'rbc-past-day' } : {};
  }, []);

  const components = useMemo(
    () => ({
      toolbar: CustomToolbar,
      month: {
        dateHeader: DateHeader,
        event: EventCard,
      },
    }),
    [DateHeader]
  );

  const handleEventDrop = useCallback(
    ({ event, start }: { event: AgendaEvent; start: Date }) => {
      onDropEvent(event.resource, start);
    },
    [onDropEvent]
  );

  return (
    <div className="agenda-calendar-wrapper mt-3">
      <div className="agenda-calendar-legend">
        <span className="agenda-calendar-legend__item">
          <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: ESITO_COLOR.POSITIVO }} />
          {t('Positivo')}
        </span>
        <span className="agenda-calendar-legend__item">
          <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: ESITO_COLOR.NEUTRO }} />
          {t('Neutro')}
        </span>
        <span className="agenda-calendar-legend__item">
          <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: ESITO_COLOR.NEGATIVO }} />
          {t('Negativo')}
        </span>
        <span className="agenda-calendar-legend__item">
          <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: ESITO_COLOR.ANNULLATA }} />
          {t('Annullata')}
        </span>
        <span className="agenda-calendar-legend__item">
          <span className="agenda-calendar-legend__swatch" style={{ backgroundColor: PENDING_BG }} />
          {t('Da eseguire')}
        </span>
      </div>
      <DnDCalendar
        localizer={localizer}
        events={events}
        date={date}
        onNavigate={onNavigate}
        startAccessor="start"
        endAccessor="end"
        defaultView={Views.MONTH}
        views={[Views.MONTH]}
        messages={messages}
        eventPropGetter={eventStyleGetter}
        dayPropGetter={dayPropGetter}
        components={components}
        style={{ minHeight: 600 }}
        popup
        onSelectEvent={(event: AgendaEvent) => onSelectEvent(event.resource)}
        draggableAccessor={(event: AgendaEvent) => !event.resource.esito}
        onEventDrop={handleEventDrop}
        resizable={false}
      />
    </div>
  );
};

export default AgendaActionsCalendar;
